/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderUserContext } from '@gh/builder-shared';
import { ScheduleType } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  markFormStateAsTouched,
  PlainImmutablePageState,
} from '@gh/common-shared';
import { ID, WithUniqueId } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { switchMap$ } from '@gh/rx/operators';
import { find, get, sortBy } from 'lodash';

import {
  createTaskNote,
  createTaskTodo,
  getScheduleTaskDefaultGeneralSubTab,
  getScheduleTaskNoteId,
  getScheduleTaskTodoId,
  SCHEDULE_TASK_DETAILS_INIT,
  ScheduleTaskDetails,
  ScheduleTaskDetailsGeneralSubtabName,
  ScheduleTaskDetailsTabName,
  ScheduleTaskInfo,
  scheduleTaskNoteArr,
  ScheduleTaskNoteListItem,
  ScheduleTaskRelatedResourceTaskListItem,
  scheduleTaskTodoArr,
  ScheduleTaskTodoListItem,
  validateScheduleTaskInfo,
} from '../../resources/schedule-task-details.model';

@Injectable()
export class ScheduleTaskDetailsFormState extends PlainImmutablePageState<ScheduleTaskDetails> {

  info$ = this.select(({ info }: ScheduleTaskDetails) => info);
  notes$ = this.select(({ notes }: ScheduleTaskDetails) => notes);
  todo$ = this.select(({ todo }: ScheduleTaskDetails) => todo);
  selectedTab$ = this.select(({ selectedTab }: ScheduleTaskDetails) => selectedTab);
  selectedGeneralSubtab$ = this.select(({ selectedGeneralSubtab }: ScheduleTaskDetails) => selectedGeneralSubtab);
  selectedUserResource$ = this.select(({ userId }) => userId);
  selectedCostCenterResource$ = this.select(({ supplierCostCenterId, supplierContactId }) =>
    `${supplierContactId}${supplierCostCenterId}`);
  currentTasks$ = combineLatest$(
    this.selectedGeneralSubtab$,
    this.selectedUserResource$,
    this.selectedCostCenterResource$).pipe(
    switchMap$(() =>
      this.select(({ supplierCostCenterId, supplierContactId, userId, selectedGeneralSubtab, employeeResources, ccResources }: ScheduleTaskDetails) =>
        selectedGeneralSubtab === ScheduleTaskDetailsGeneralSubtabName.UserResource
          ? get(find(employeeResources, { userId }), 'currentTasks', [])
          : get(find(ccResources, { supplierId: supplierContactId, costCenterId: supplierCostCenterId }),
                'currentTasks',
                []))));

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext) {
    super(SCHEDULE_TASK_DETAILS_INIT);
  }

  get info(): FormStateSnapshot<ScheduleTaskInfo> {
    return this.project(({ info }: ScheduleTaskDetails) => info);
  }

  get name(): Maybe<string> {
    return getFormStateSnapshotValue(this.info).name;
  }

  get type(): ScheduleType {
    return getFormStateSnapshotValue(this.info).scheduleType;
  }

  isTopFormInvalid(confirmedForDate: Maybe<Date>, confirmedOnDate: Maybe<Date>): boolean {
    return isNotNil(
      validateScheduleTaskInfo(
        confirmedForDate,
        confirmedOnDate,
      )(getFormStateSnapshotValue(this.value.info)));
  }

  get selectedTab(): ScheduleTaskDetailsTabName {
    return this.project(({ selectedTab }: ScheduleTaskDetails) => selectedTab);
  }

  get selectedGeneralSubtab(): Maybe<ScheduleTaskDetailsGeneralSubtabName> {
    return this.project(({ selectedGeneralSubtab }: ScheduleTaskDetails) => selectedGeneralSubtab);
  }

  get jobNumber(): string {
    return this.value.jobNumber;
  }

  get salesFolderName(): string {
    return this.value.salesFolderName;
  }

  highlightInfoSnapshot(): void {
    this.setField('info', markFormStateAsTouched(this.value.info));
  }

  build(taskDetails: ScheduleTaskDetails, isEditMode: boolean): void {
    this.setMode(isEditMode ? FormMode.EditMode : FormMode.ViewMode);
    this.setValue({
      ...taskDetails,
      selectedTab: this.selectedTab || ScheduleTaskDetailsTabName.General,
      selectedGeneralSubtab: this.selectedGeneralSubtab || getScheduleTaskDefaultGeneralSubTab(taskDetails),
    });
    this.resetDirty();
  }

  selectTab(tab: ScheduleTaskDetailsTabName): void {
    this.setField('selectedTab', tab);
  }

  selectGeneralSubtab(subtab: ScheduleTaskDetailsGeneralSubtabName): void {
    this.setField('selectedGeneralSubtab', subtab);
  }

  updateTaskInfoSnapshot(infoSnapshot: FormStateSnapshot<ScheduleTaskInfo>): void {
    this.setField('info', markFormStateAsTouched(infoSnapshot));
    this.setDirty(true);
  }

  updateCurrentTasks(currentTasks: ScheduleTaskRelatedResourceTaskListItem[]): void {
    this.setField('currentTasks', sortBy(currentTasks, 'startDate'));
    // do not set Dirty
  }

  updateSupplierContactId(supplierCostCenterId: Maybe<ID>, supplierContactId: Maybe<ID>): void {
    this.startChangeDetection();
    this.setFields({ supplierContactId, supplierCostCenterId });
    this.setDirtyIfChanged();
  }

  updateUserId(userId: Maybe<ID>): void {
    this.startChangeDetection();
    this.setField('userId', userId);
    this.setDirtyIfChanged();
  }

  removeNote(note: WithUniqueId<ScheduleTaskNoteListItem>): void {
    this.startChangeDetection();
    this.setField('notes', scheduleTaskNoteArr.remove(this.value.notes, getScheduleTaskNoteId(note)));
    this.setDirtyIfChanged();
  }

  updateNote(note: WithUniqueId<ScheduleTaskNoteListItem>,
             { memo }: ScheduleTaskNoteListItem): void {
    this.startChangeDetection();
    this.setField(
      'notes',
      scheduleTaskNoteArr.update(
        this.value.notes,
        getScheduleTaskNoteId(note), {
          ...note,
          memo,
          addedBy: this.userContext.data.fullName,
        },
      ));
    this.setDirtyIfChanged();
  }

  addNote({ memo }: ScheduleTaskNoteListItem): void {
    this.startChangeDetection();
    this.setField(
      'notes',
      scheduleTaskNoteArr.append(
        this.value.notes,
        createTaskNote(memo, this.userContext.data.fullName),
      ));
    this.setDirtyIfChanged();
  }

  addTodo(todo: ScheduleTaskTodoListItem): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTaskTodoArr.append(this.value.todo, createTaskTodo(todo)));
    this.setDirtyIfChanged();
  }

  updateTodo(todo: WithUniqueId<ScheduleTaskTodoListItem>,
             updatedTodo: ScheduleTaskTodoListItem): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTaskTodoArr.update(
      this.value.todo,
      getScheduleTaskTodoId(todo), {
        ...todo,
        ...updatedTodo,
      }));
    this.setDirtyIfChanged();
  }

  removeTodo(todo: WithUniqueId<ScheduleTaskTodoListItem>): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTaskTodoArr.remove(this.value.todo, getScheduleTaskTodoId(todo)));
    this.setDirtyIfChanged();
  }
}
